import { type ReactNode } from 'react';
import { usePageContext } from '@/hooks/useRouter';
import { navigate } from '@/hooks/useRouter';
import { Home, Gift, MessageSquare, Headphones, User } from 'lucide-react';
import Tabbar, { type TabItem } from '@/components/ui/Tabbar';

/**
 * Tab 配置项
 */
interface TabConfig {
  name: string;
  label: string;
  path: string;
  icon: typeof Home;
}

/**
 * 统一的 Tab 配置
 */
const TAB_CONFIG: TabConfig[] = [
  { name: 'home', label: '首页', path: '/', icon: Home },
  { name: 'activity', label: '活动', path: '/activity', icon: Gift },
  { name: 'chatroom', label: '群聊', path: '/chatroom', icon: MessageSquare },
  { name: 'support', label: '客服', path: '/customer-service', icon: Headphones },
  { name: 'profile', label: '我的', path: '/profile', icon: User },
];

interface TabbarLayoutProps {
  children: ReactNode;
  /** 手动指定激活的 tab，不指定则根据路由自动判断 */
  activeTab?: string;
  /** 额外的容器 className */
  className?: string;
  /** 底部导航主题 */
  tabbarVariant?: 'light' | 'dark';
}

/**
 * 带底部导航栏的页面布局组件
 */
export default function TabbarLayout({ children, activeTab, className, tabbarVariant = 'light' }: TabbarLayoutProps) {
  const pageContext = usePageContext();
  const currentPath = pageContext.urlPathname;

  // 根据当前路径自动判断激活的 tab
  const getActiveTab = (): string => {
    if (activeTab) return activeTab;

    // 精确匹配或前缀匹配
    for (const config of TAB_CONFIG) {
      if (config.path === '/') {
        if (currentPath === '/') return config.name;
      } else if (currentPath.startsWith(config.path)) {
        return config.name;
      }
    }
    return 'home';
  };

  const handleTabChange = (name: string) => {
    const config = TAB_CONFIG.find((c) => c.name === name);
    if (config) {
      navigate(config.path);
    }
  };

  const isDarkTabbar = tabbarVariant === 'dark';

  const tabItems: TabItem[] = TAB_CONFIG.map((config) => ({
    name: config.name,
    label: config.label,
    icon: (active: boolean) => {
      const Icon = config.icon;
      const color = isDarkTabbar ? (active ? '#ffffff' : 'rgba(255,255,255,0.68)') : (active ? '#20b2aa' : '#969799');
      return <Icon size={24} color={color} />;
    },
  }));

  return (
    <div className={`min-h-screen bg-bg-page pb-16 ${className || ''}`}>
      {children}
      <Tabbar items={tabItems} active={getActiveTab()} onChange={handleTabChange} variant={tabbarVariant} />
    </div>
  );
}
